import { Search, Settings, Keyboard, RefreshCw } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { useChannelsStore } from '@/store/useChannelsStore';

export function TopBar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isRefreshing = useChannelsStore((s) => s.isRefreshing);
  const refresh = useChannelsStore((s) => s.refresh);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const openHelp = () => {
    // Dispatch the same shortcut the keyboard handler listens for.
    window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  return (
    <header className="h-14 bg-bg-elevated border-b border-border flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
      <Link to="/" className="flex items-center gap-2" aria-label="StreamPitch Home">
        <svg className="w-6 h-6 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="2" y="2" width="20" height="20" rx="4" />
          <path d="M8 14 L11 11 L16 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="font-semibold text-lg tracking-tight">StreamPitch</span>
      </Link>

      <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-4 lg:mx-8 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" aria-hidden="true" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search channels, countries, sports..."
          className="w-full h-9 pl-10 pr-4 bg-bg rounded-lg border border-border text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
          aria-label="Search channels"
        />
      </form>

      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            void refresh();
          }}
          disabled={isRefreshing}
          className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg bg-bg hover:bg-bg-hover border border-border flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Refresh channels"
          title="Refresh channels"
        >
          <RefreshCw
            className={`w-5 h-5 text-text-muted ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </button>

        <button
          onClick={openHelp}
          className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg bg-bg hover:bg-bg-hover border border-border flex items-center justify-center transition-colors"
          aria-label="Keyboard shortcuts"
          title="Keyboard shortcuts (?)"
        >
          <Keyboard className="w-5 h-5 text-text-muted" />
        </button>

        <div className="relative" ref={settingsRef}>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg bg-bg hover:bg-bg-hover border border-border flex items-center justify-center transition-colors"
            aria-label="Settings"
            aria-expanded={showSettings}
          >
            <Settings className="w-5 h-5 text-text-muted" />
          </button>

          {showSettings && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-bg-elevated border border-border rounded-lg shadow-lg py-1 z-50">
              <Link
                to="/settings"
                className="block px-4 py-2 text-sm hover:bg-bg-hover transition-colors"
                onClick={() => setShowSettings(false)}
              >
                Settings
              </Link>
              <button
                onClick={() => {
                  setShowSettings(false);
                  openHelp();
                }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-bg-hover transition-colors"
              >
                Keyboard shortcuts
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}